/**
 * Заголовок, описание и канонический адрес — по маршруту, а не один на сайт.
 *
 * ЧТО БЫЛО. Одностраничное приложение отдаёт один и тот же `<title>` и одно
 * описание всем: и «Курсам», и «Книгам», и карточке психолога. Для поиска это
 * означает, что десяток разных страниц выглядит одной, а для ИИ-поисковиков —
 * что цитировать нечего: страница называется «Global Psychology Hub» и говорит
 * о себе то же самое, что и все остальные.
 *
 * ЧТО ЗДЕСЬ. Таблица «путь → заголовок и описание» на двух языках и одна
 * функция, которая кладёт их в документ вместе с canonical и Open Graph.
 * Страница, у которой есть собственное имя (карточка психолога, схема по
 * ссылке), зовёт `setPageMeta` сама.
 *
 * ЭТА ЖЕ ТАБЛИЦА собирает статику. На сборке `scripts/prerender.mjs` прогоняет
 * публичные адреса через те же компоненты и кладёт готовый HTML рядом с
 * бандлом, беря отсюда заголовок, описание и canonical, — поэтому краулеру,
 * который не исполняет JS, страница отвечает содержимым, а не пустым `#root`.
 */
import { isLocalizedZone } from '@/i18n/lang-path'
import type { Lang } from '@/i18n/translations'
import { userPageHandle } from '@/shared/site-paths'

/** Канонический origin. Один на приложение: адреса в мете абсолютные. */
export const SITE_URL = 'https://hub4psy.com'

export interface PageMeta {
  title: string
  description: string
  /** Абсолютный адрес картинки для соцсетей; по умолчанию — обложка сайта. */
  image?: string
  /** `noindex` для страниц, которым нечего делать в выдаче. */
  noindex?: boolean
  /**
   * `<meta name="keywords">` — только у страницы, которой есть что назвать:
   * специализации психолога, имя человека. Пустой список тега не пишет, и
   * тег предыдущей страницы снимается — иначе слова психолога уезжали бы
   * вместе с читателем на «Курсы».
   */
  keywords?: string[]
  /**
   * `og:type`: страница человека — `profile`, всё остальное — `website`
   * (по умолчанию). У `profile` мессенджеры и FB читают `profile:username`.
   */
  ogType?: 'website' | 'profile'
  /** Имя аккаунта на площадке — для `profile:username` при `ogType: 'profile'`. */
  username?: string
}

/**
 * Карточка для соцсетей — 1200×630 и на языке страницы.
 *
 * Раньше здесь стояла обложка книги 700×990: в ленте `summary_large_image` её
 * или обрезало по краям, или ужимало до марки на пустом поле. Обе карточки
 * рисует `scripts/og_images.py` из тех же цветов и фигур, что и приложение.
 */
export const COVER_IMAGE: Record<Lang, string> = {
  en: `${SITE_URL}/og-cover.png`,
  ru: `${SITE_URL}/og-cover-ru.png`,
}

/** Размер карточек — обеих; соцсети рисуют место под превью до загрузки файла. */
export const OG_IMAGE_SIZE = { width: 1200, height: 630 }

/**
 * Ссылка на схему (`/s/<token>`) — единственная страница, которую отправляют
 * ЧЕЛОВЕКУ, а не поисковику: она нескрываемо приватная (`noindex`, как и весь
 * `/s/`), но превью в мессенджере у неё быть обязано — иначе рядом со ссылкой
 * стоит карточка главной страницы и не говорит ничего.
 *
 * На главном языке сайта: адрес токена языка не несёт, а заводить второй
 * адресный простор ради него значило бы разослать людям ссылки, которые
 * различаются буквами `ru`.
 */
export const SHARE_META: PageMeta = {
  title: 'A manipulation scheme — AHM',
  description:
    'Who did what, in which order, and what it cost each of them. Open the scheme and play it '
    + 'back beat by beat — it assembles in the order it happened.',
  image: `${SITE_URL}/og-scheme.png`,
  noindex: true,
}

/**
 * Ключевые слова главной — те же, что стоят в шаблоне `index.html`, только на
 * языке страницы. Тег в шаблоне — заглушка, как `og:title`: сборка и приложение
 * переписывают его по этой таблице, а у страницы без своего списка снимают.
 * До этого все двадцать страниц статики несли слова главной, и русская — по-
 * английски.
 */
const HOME_KEYWORDS: Record<Lang, string[]> = {
  en: [
    'AHM', 'Alphabet of the Human Mind', 'Azbooka chelovecheskoy psikhiki', 'psychology', 'courses',
    'Danilov', 'manipulation diagram', 'psychologist',
  ],
  ru: ['АЧП', 'Азбука Человеческой Психики', 'психология', 'курсы', 'Данилов', 'схема манипуляции', 'психолог'],
}

const RU: Record<string, PageMeta> = {
  '/practice': { title: 'Практика психолога — HUB4PSY', description: 'Своя страница психолога, клиенты, расписание и профессиональные инструменты. Откройте практику в пространстве АЧП.' },
  '/': {
    title: 'HUB4PSY — Азбука Человеческой Психики',
    description:
      'АЧП: система, которая объясняет манипуляции и психотипы простым языком. Книги, '
      + 'курсы, моделлер схем манипуляций и каталог сертифицированных психологов.',
    keywords: HOME_KEYWORDS.ru,
  },
  '/courses': {
    title: 'Курсы АЧП — от «Кик старта» до сертификации',
    description:
      'Курсы по Азбуке Человеческой Психики: разбор психотипов, схем манипуляций и '
      + 'защиты. Для себя и для практикующих психологов.',
  },
  '/books': {
    title: 'Книги АЧП — Азбука Человеческой Психики, тома 1 и 2',
    description:
      'Две книги, из которых выросла система: психотипы (ОСМ, ТЛЧ, КПМ), потоки '
      + 'симптомов и схемы манипуляций — языком, который понятен без образования.',
  },
  '/classification': {
    title: 'Классификация АЧП — глоссарий и справочник терминов из книг',
    description:
      'Глоссарий и классификация «Азбуки Человеческой Психики», тома 1 и 2: психотипы, стадии '
      + 'диагностики, костыли, потоки, составные симптомы и виды психотерапии — термин за термином.',
  },
  '/app': {
    title: 'Приложение АЧП — кабинет, моделлер схем, психологи',
    description:
      'Личный психо-CRM, моделлер схем манипуляций и связь с психологом: что уже '
      + 'работает и что появится дальше.',
  },
  '/alphabet': {
    title: 'Словарь схемы АЧП — психотипы, потоки, составные симптомы',
    description:
      'Всё, из чего состоит схема манипуляции: шесть силуэтов психотипов, девять видов потока и '
      + 'составные симптомы с ценой обеим сторонам в процентах суточной нормы.',
  },
  '/certification': {
    title: 'Сертификация психологов АЧП',
    description:
      'Три уровня сертификации АЧП: базовый, практикующий специалист, мастер. '
      + 'Требования, сроки и что даёт каждый уровень.',
  },
  '/find-psychologist': {
    title: 'Найти психолога АЧП — каталог специалистов',
    description:
      'Каталог психологов, работающих по АЧП: поиск по имени и специализации, фильтры '
      + 'по языку, формату и цене, запись на сессию.',
  },
  '/about': {
    title: 'Об АЧП — Никита и Екатерина Даниловы',
    description: 'Кто и зачем написал Азбуку Человеческой Психики, и как из книг вырос продукт.',
  },
  '/contact': {
    title: 'Контакты HUB4PSY',
    description: 'Как связаться с командой АЧП: вопросы по курсам, сертификации и приложению.',
  },
  '/privacy': { title: 'Политика конфиденциальности — HUB4PSY', description: 'Какие данные собирает HUB4PSY, зачем и на каком основании.', noindex: true },
  '/terms': { title: 'Условия использования — HUB4PSY', description: 'Условия использования сервисов HUB4PSY.', noindex: true },
  '/psymodeler': {
    title: 'Моделлер схем манипуляций — АЧП',
    description:
      'Нарисуйте, что произошло: психотипы, потоки симптомов, составные симптомы и цена '
      + 'в энергии. Бесплатно и без регистрации.',
  },
  '/language': {
    title: 'Язык манипуляций — спецификация схем АЧП',
    description:
      'Как записать схему манипуляции строками: разделы, психотипы, потоки, цена энергии. '
      + 'Отдельная инструкция для ИИ — скопировать и вставить в чат.',
  },
}

const EN: Record<string, PageMeta> = {
  '/practice': { title: 'Your psychology practice — HUB4PSY', description: 'Your psychologist page, clients, schedule and professional tools. Open your practice in the AHM community.' },
  '/': {
    title: 'HUB4PSY — the Alphabet of the Human Mind',
    description:
      'AHM: a system that explains manipulations and psychotypes in plain words. Books, '
      + 'courses, a manipulation-scheme modeler and a catalog of certified psychologists.',
    keywords: HOME_KEYWORDS.en,
  },
  '/courses': { title: 'AHM courses — from Kick Start to certification', description: 'Courses on the Alphabet of the Human Mind: psychotypes, manipulation schemes and defence.' },
  '/books': { title: 'AHM books — the Alphabet of the Human Mind, volumes 1 and 2', description: 'The two books the system grew from: psychotypes, symptom flows and manipulation schemes.' },
  '/classification': {
    title: 'The AHM classification — the glossary and reference of terms',
    description:
      'The glossary and classification of the Alphabet of the Human Mind, volumes 1 and 2: '
      + 'psychotypes, the stages of diagnosis, crutches, flows, composite symptoms and kinds of '
      + 'psychotherapy, term by term.',
  },
  '/app': { title: 'The AHM app — cabinet, scheme modeler, psychologists', description: 'A personal psycho-CRM, a manipulation-scheme modeler and a way to reach a psychologist.' },
  '/alphabet': {
    title: 'The AHM scheme vocabulary — psychotypes, flows, composite symptoms',
    description:
      'Everything a manipulation scheme is made of: six psychotype silhouettes, nine kinds of flow, '
      + 'and the composite symptoms with what each costs both sides.',
  },
  '/certification': { title: 'AHM psychologist certification', description: 'Three levels of AHM certification: basic, practitioner and master.' },
  '/find-psychologist': { title: 'Find an AHM psychologist — the catalog', description: 'Psychologists working with AHM: search by name and specialty, filter by language, format and price.' },
  '/about': { title: 'About AHM — Nikita and Ekaterina Danilov', description: 'Who wrote the Alphabet of the Human Mind, and how a product grew out of the books.' },
  '/contact': { title: 'Contact HUB4PSY', description: 'How to reach the AHM team about courses, certification and the app.' },
  '/privacy': { title: 'Privacy policy — HUB4PSY', description: 'What HUB4PSY collects, why, and on what basis.', noindex: true },
  '/terms': { title: 'Terms of use — HUB4PSY', description: 'Terms of use for HUB4PSY services.', noindex: true },
  '/psymodeler': { title: 'Manipulation scheme modeler — AHM', description: 'Draw what happened: psychotypes, symptom flows, composite symptoms and the price in energy. Free, no account.' },
  '/language': { title: 'The manipulation language — the AHM scheme spec', description: 'How to write a manipulation scheme as lines, plus a briefing to paste into an AI chat.' },
}

/**
 * Заголовки вкладок ПРИЛОЖЕНИЯ — по разделу и на языке интерфейса.
 *
 * Раньше личные зоны получали заголовок главной, и русский кабинет на каждой
 * вкладке писал английский слоган «HUB4PSY — the Alphabet of the Human Mind»:
 * пять открытых вкладок кабинета нельзя было отличить друг от друга. В выдачу
 * эти страницы не попадают (`noindex`), так что заголовок здесь — для
 * человека: раздел, тире, имя площадки. Совпадение — по самому длинному
 * префиксу, поэтому карточка человека — «Люди», а неизвестный адрес внутри
 * зоны — имя зоны. Имена зон — те же, что в переключателе (`workspaces.ts`).
 */
type AppSection = readonly [prefix: string, ru: string, en: string]

const APP_SECTION_TABLE: readonly AppSection[] = [
  ['/cabinet/practice/profile', 'Профиль психолога', 'Psychologist profile'],
  ['/cabinet/practice/page', 'Страница психолога', 'Psychologist page'],
  ['/cabinet/practice/clients', 'Клиенты', 'Clients'],
  ['/cabinet/practice/schedule', 'Расписание', 'Schedule'],
  ['/cabinet/practice/sessions', 'Сессии', 'Sessions'],
  ['/cabinet/practice/messages', 'Сообщения', 'Messages'],
  ['/cabinet/practice', 'Моя практика', 'My practice'],
  ['/cabinet/people', 'Люди', 'People'],
  ['/cabinet/circles', 'Круги', 'Circles'],
  ['/cabinet/groups', 'Группы', 'Groups'],
  ['/cabinet/situations', 'Ситуации', 'Situations'],
  ['/cabinet/schemes', 'Схемы', 'Schemes'],
  ['/cabinet/settings', 'Настройки', 'Settings'],
  ['/cabinet/moderation', 'Модерация', 'Moderation'],
  ['/cabinet', 'Книга друзей', 'Friends book'],
  ['/my/sessions', 'Сессии', 'Sessions'],
  ['/my/messages', 'Сообщения', 'Messages'],
  ['/my/psychologists', 'Мои психологи', 'My psychologists'],
  ['/my/settings', 'Настройки', 'Settings'],
  ['/my', 'Мои встречи', 'My therapy'],
  ['/signin', 'Вход', 'Sign in'],
  ['/login', 'Вход', 'Sign in'],
  ['/sign-in', 'Вход', 'Sign in'],
  ['/register', 'Регистрация', 'Create an account'],
  ['/signup', 'Регистрация', 'Create an account'],
  ['/sign-up', 'Регистрация', 'Create an account'],
  ['/verify-email', 'Подтверждение адреса', 'Confirming your address'],
  ['/forgot-password', 'Восстановление пароля', 'Password recovery'],
  ['/reset-password', 'Новый пароль', 'New password'],
]

/** Самый длинный префикс — первым: профиль психолога не должен назваться именем всей практики. */
const APP_SECTIONS = [...APP_SECTION_TABLE].sort((a, b) => b[0].length - a[0].length)

/** Точные адреса, чьё имя не совпадает с именем зоны: главная кабинета — «Обзор». */
const APP_EXACT: Record<string, readonly [ru: string, en: string]> = {
  '/cabinet': ['Обзор', 'Overview'],
}

/** Заголовок вкладки приложения, или `null` для адреса вне личных зон. */
export function appTitle(path: string, lang: Lang): string | null {
  const section = APP_SECTIONS.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`))
  const names = APP_EXACT[path] ?? (section ? ([section[1], section[2]] as const) : null)
  if (!names) return null
  return `${lang === 'ru' ? names[0] : names[1]} — HUB4PSY`
}

/**
 * Непрочитанные — числом перед заголовком: «(2) Сообщения — HUB4PSY». Только
 * в личных зонах: витрине нечего считать, а в выдачу число попасть не должно.
 */
export function withUnreadCount(meta: PageMeta, count: number, path: string): PageMeta {
  if (count <= 0 || !isPrivatePath(path)) return meta
  return { ...meta, title: `(${count > 99 ? '99+' : count}) ${meta.title}` }
}

/**
 * Страницы, которым в выдаче делать нечего: личные зоны и всё, что за
 * авторизацией. Здесь — чтобы `robots.txt` и мета говорили одно и то же.
 */
export const PRIVATE_PREFIXES = [
  '/cabinet',
  '/my',
  '/signin',
  '/register',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
  '/s/',
  // Переадресации на вход (router.tsx, `authAliases`): в выдаче им делать нечего,
  // а без JS краулер увидел бы на них оболочку с мета главной — её копию.
  '/login',
  '/sign-in',
  '/signup',
  '/sign-up',
]

export function isPrivatePath(path: string): boolean {
  return PRIVATE_PREFIXES.some((prefix) => path.startsWith(prefix))
}

/**
 * Публичные адреса — на ОБОИХ языках: у русской версии свои страницы, и в карте
 * сайта они должны быть, иначе индексируется половина сайта.
 */
const BASE_PATHS = Object.keys(EN).filter((path) => !EN[path].noindex)
export const PUBLIC_PATHS = [
  ...BASE_PATHS,
  // Только те, у кого русский адрес ЕСТЬ. У моделлера и страницы языка его нет
  // (они вне витрины, язык там из браузера), и `/ru/psymodeler` отдавал 200 со
  // словами «Not Found»: страница пререндерилась, попадала в карту сайта и
  // приходила в Search Console мягкой 404.
  ...BASE_PATHS.filter((path) => isLocalizedZone(path)).map((path) => pathInLang(path, 'ru')),
]

/** Язык страницы задаёт её адрес: `/ru/...` — русская, остальное — английская. */
export function langOfPath(path: string): Lang {
  return path === '/ru' || path.startsWith('/ru/') ? 'ru' : 'en'
}

/** Тот же адрес на другом языке — для hreflang и переключателя. */
export function pathInLang(path: string, lang: Lang): string {
  const bare = langOfPath(path) === 'ru' ? path.slice(3) || '/' : path
  if (lang === 'en') return bare
  return bare === '/' ? '/ru' : `/ru${bare}`
}

export function metaFor(path: string, lang: Lang = langOfPath(path)): PageMeta {
  if (path === '/design-system') return { title: 'HUB4PSY · UI kit', description: 'HUB4PSY interface principles and live components.', noindex: true }
  const table = lang === 'ru' ? RU : EN
  const withoutPrefix = langOfPath(path) === 'ru' ? path.slice(3) || '/' : path
  const clean = withoutPrefix.length > 1 ? withoutPrefix.replace(/\/+$/, '') : withoutPrefix
  if (table[clean]) return table[clean]

  if (clean === '/s' || clean.startsWith('/s/')) return SHARE_META
  // Страница аккаунта — на корне, `/anna.k` (с 29.09.2026 у любого аккаунта):
  // своё имя ставит сама страница (`usePageMeta` — правило психолога в
  // `features/profile-page/meta.ts`, человека — в `features/user-page/meta.ts`),
  // но до её загрузки выдача не должна видеть заголовок главной. Она и оба
  // прежних адреса психолога — `/p/<handle>` и `/find-psychologist/<handle>`,
  // которые переадресуют на корень, — падают на описание каталога.
  if (clean.startsWith('/p/') || clean.startsWith('/find-psychologist/') || userPageHandle(clean)) {
    return table['/find-psychologist']
  }
  if (isPrivatePath(clean)) {
    return {
      title: appTitle(clean, lang) ?? table['/'].title,
      description: table['/'].description,
      noindex: true,
    }
  }
  return table['/']
}

/**
 * Структурированные данные о ЧЕЛОВЕКЕ — для страницы психолога и для
 * включённой страницы пользователя.
 *
 * Отдельный узел `<script id="page-graph">`, а не правка статического JSON-LD в
 * `index.html`: тот описывает организацию и сайт и одинаков на всех страницах,
 * а этот появляется и исчезает вместе с конкретным профилем. `@id` ссылается на
 * узел организации из шапки, поэтому для поисковика это не два независимых
 * объекта, а человек В КОНТЕКСТЕ площадки: психолог — `jobTitle` и `worksFor`,
 * человек без профессии — только `memberOf`. Приписать пользователю
 * «Psychologist» значило бы солгать поисковику о каждом, кто включил страницу.
 *
 * `aggregateRating` здесь НЕТ, и это решение: звёздный сниппет в выдаче при
 * малом числе отзывов — одновременно ложь с высоким CTR и прямое приглашение к
 * накрутке, то есть ровно тот стимул, против которого написано правило «каталог
 * никогда не сортируется по рейтингу».
 */
export function personGraph(person: {
  name: string
  url: string
  headline?: string
  image?: string
  /** Профессия — только у психолога; без неё узел описывает просто человека. */
  jobTitle?: string
  /** Темы, в которых человек разбирается, — специализации психолога на языке страницы. */
  knowsAbout?: string[]
  city?: string
  country?: string
  languages?: string[]
}): string {
  const org = { '@id': `${SITE_URL}/#org` }
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: {
      '@type': 'Person',
      name: person.name,
      url: person.url,
      ...(person.headline ? { description: person.headline } : {}),
      ...(person.image ? { image: person.image } : {}),
      ...(person.jobTitle ? { jobTitle: person.jobTitle } : {}),
      ...(person.knowsAbout?.length ? { knowsAbout: person.knowsAbout } : {}),
      ...(person.languages?.length ? { knowsLanguage: person.languages } : {}),
      ...(person.city || person.country
        ? {
            address: {
              '@type': 'PostalAddress',
              ...(person.city ? { addressLocality: person.city } : {}),
              ...(person.country ? { addressCountry: person.country } : {}),
            },
          }
        : {}),
      ...(person.jobTitle ? { worksFor: org } : { memberOf: org }),
    },
  })
}

/** Адрес картинки, как его отдаёт API (`/api/public/...`), — абсолютным, для соцсетей. */
export function absoluteUrl(url: string): string {
  return /^https?:\/\//.test(url) ? url : `${SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`
}

/** `og:locale` страницы и её второй половины. */
const OG_LOCALE: Record<Lang, string> = { en: 'en_US', ru: 'ru_RU' }

/** Вторая половина страницы существует — той же проверкой, что ставит hreflang. */
export function hasOtherLanguage(path: string, noindex: boolean): boolean {
  return !noindex && isLocalizedZone(path)
}

/** Ставит (или снимает) граф страницы. Один узел, переиспользуется. */
export function applyGraph(graph: string | null): void {
  const id = 'page-graph'
  let tag = document.getElementById(id) as HTMLScriptElement | null
  if (!graph) {
    tag?.remove()
    return
  }
  if (!tag) {
    tag = document.createElement('script')
    tag.id = id
    tag.type = 'application/ld+json'
    document.head.appendChild(tag)
  }
  tag.textContent = graph
}

/**
 * Кладёт мету в документ. Пишет, а не добавляет: теги переиспользуются, иначе
 * переход между страницами оставлял бы за собой десяток canonical.
 */
export function applyMeta(meta: PageMeta, path: string, lang: Lang = langOfPath(path)): void {
  // Хвостовой слэш срезается: /courses/ и /courses — одна страница, и canonical
  // обязан называть одну. Статика лежит по /courses/index.html, поэтому адрес
  // со слэшем встречается в реальной жизни, а не только в теории.
  const clean = path.length > 1 ? path.replace(/\/+$/, '') : path
  // Корень — со слэшем: «https://hub4psy.com» и «…/» поисковик считает одним
  // адресом, но canonical принято писать полным.
  const url = `${SITE_URL}${clean === '/' ? '/' : clean}`
  const image = meta.image ?? COVER_IMAGE[lang]
  const noindex = meta.noindex === true
  const ogType = meta.ogType ?? 'website'
  document.title = meta.title
  setMeta('name', 'description', meta.description)
  // Ключевые слова — только там, где они есть; чужие снимаются.
  if (meta.keywords?.length) setMeta('name', 'keywords', meta.keywords.join(', '))
  else removeMeta('name', 'keywords')
  setMeta('property', 'og:site_name', 'HUB4PSY')
  setMeta('property', 'og:title', meta.title)
  setMeta('property', 'og:description', meta.description)
  setMeta('property', 'og:url', url)
  setMeta('property', 'og:type', ogType)
  if (ogType === 'profile' && meta.username) setMeta('property', 'profile:username', meta.username)
  else removeMeta('property', 'profile:username')
  setMeta('property', 'og:locale', OG_LOCALE[lang])
  // Вторая локаль — ровно там, где есть второй адрес (то же условие, что у
  // hreflang): обещать русскую карточку моделлеру значит обещать страницу,
  // которой нет.
  if (hasOtherLanguage(clean, noindex)) setMeta('property', 'og:locale:alternate', OG_LOCALE[lang === 'ru' ? 'en' : 'ru'])
  else removeMeta('property', 'og:locale:alternate')
  setMeta('property', 'og:image', image)
  // Размеры — чтобы лента нарисовала место под карточку до того, как файл
  // приедет: без них превью «прыгает», а часть площадок молча его пропускает.
  setMeta('property', 'og:image:width', String(OG_IMAGE_SIZE.width))
  setMeta('property', 'og:image:height', String(OG_IMAGE_SIZE.height))
  setMeta('property', 'og:image:alt', meta.title)
  setMeta('name', 'twitter:card', 'summary_large_image')
  setMeta('name', 'twitter:title', meta.title)
  setMeta('name', 'twitter:description', meta.description)
  setMeta('name', 'twitter:image', image)
  setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow')
  setCanonical(url)
  setAlternates(clean, noindex)
}

/**
 * hreflang: у страницы два адреса, и поисковик должен знать, что это одна
 * страница на двух языках, а не две конкурирующие. `x-default` — английский:
 * он главный и живёт в корне.
 */
function setAlternates(path: string, noindex: boolean): void {
  for (const link of document.head.querySelectorAll('link[rel="alternate"][hreflang]')) {
    link.remove()
  }
  // У страницы вне витрины второго адреса не существует — объявлять его значит
  // обещать поисковику страницу, которой нет.
  if (!hasOtherLanguage(path, noindex)) return
  // Корень — со слэшем, ровно как в canonical и в карте сайта: поисковик считает
  // «https://hub4psy.com» и «…/» одним адресом, но называть один адрес двумя
  // строками — значит заставлять его это выяснять.
  const en = `${SITE_URL}${pathInLang(path, 'en')}`
  const ru = `${SITE_URL}${pathInLang(path, 'ru')}`
  for (const [hreflang, href] of [['en', en], ['ru', ru], ['x-default', en]] as const) {
    const link = document.createElement('link')
    link.rel = 'alternate'
    link.hreflang = hreflang
    link.href = href
    document.head.appendChild(link)
  }
}

function setMeta(attr: 'name' | 'property', key: string, value: string): void {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attr, key)
    document.head.appendChild(tag)
  }
  tag.content = value
}

function removeMeta(attr: 'name' | 'property', key: string): void {
  document.head.querySelector(`meta[${attr}="${key}"]`)?.remove()
}

function setCanonical(url: string): void {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'canonical'
    document.head.appendChild(link)
  }
  link.href = url
}
